import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { BoilerplateDrawer } from './boilerplate_drawer';
import {
  getCookiebotConsent,
  persistAcceptedConsent,
  persistDeclinedConsent,
  persistSavedConsent,
  waitForCookiebotConsentReady,
} from '../utilities/cookies';

class DrawerCookiePreferences extends BoilerplateDrawer {
  static properties = {
    blurb_html: { type: String },
    necessary_title: { type: String },
    necessary_description: { type: String },
    preferences_title: { type: String },
    preferences_description: { type: String },
    statistics_title: { type: String },
    statistics_description: { type: String },
    marketing_title: { type: String },
    marketing_description: { type: String },
    decline_all_text: { type: String },
    accept_all_text: { type: String },
    save_preferences_text: { type: String },
    draft: { type: Object, state: true },
  };

  constructor() {
    super();
    this.position = 'right';
    this.draft = {
      preferences: false,
      statistics: false,
      marketing: false,
    };
  }

  async onDrawerOpen() {
    await this.syncDraftFromCookiebot();
  }

  async syncDraftFromCookiebot() {
    await waitForCookiebotConsentReady();
    const consent = getCookiebotConsent();
    this.draft = {
      preferences: consent.preferences,
      statistics: consent.statistics,
      marketing: consent.marketing,
    };
    this.requestUpdate();
  }

  handlePreferenceChange(category, e) {
    this.draft = {
      ...this.draft,
      [category]: e.detail.checked,
    };
  }

  handleDeclineAll() {
    persistDeclinedConsent();
    this.closeDrawer();
  }

  handleAcceptAll() {
    persistAcceptedConsent();
    this.closeDrawer();
  }

  handleSavePreferences() {
    persistSavedConsent({
      preferences: this.draft.preferences,
      statistics: this.draft.statistics,
      marketing: this.draft.marketing,
    });
    this.closeDrawer();
  }

  renderBody() {
    return html`
      <div class="cookie_preferences_blurb">${unsafeHTML(this.blurb_html)}</div>

      <div class="cookie_preferences_group">
        <button
          type="button"
          class="cookie_preferences_btn cookie_preferences_btn_accept"
          @click=${this.handleAcceptAll}
        >
          ${this.accept_all_text}
        </button>
        <button
          type="button"
          class="cookie_preferences_btn cookie_preferences_btn_decline"
          @click=${this.handleDeclineAll}
        >
          ${this.decline_all_text}
        </button>
      </div>

      <div class="cookie_preferences_categories">
        <cookie-preference-category
          title=${this.necessary_title}
          description=${this.necessary_description}
          .checked=${true}
          .disabled=${true}
        ></cookie-preference-category>

        <cookie-preference-category
          title=${this.preferences_title}
          description=${this.preferences_description}
          .checked=${this.draft.preferences}
          @cookie-preference-change=${(e) =>
            this.handlePreferenceChange('preferences', e)}
        ></cookie-preference-category>

        <cookie-preference-category
          title=${this.statistics_title}
          description=${this.statistics_description}
          .checked=${this.draft.statistics}
          @cookie-preference-change=${(e) =>
            this.handlePreferenceChange('statistics', e)}
        ></cookie-preference-category>

        <cookie-preference-category
          title=${this.marketing_title}
          description=${this.marketing_description}
          .checked=${this.draft.marketing}
          @cookie-preference-change=${(e) =>
            this.handlePreferenceChange('marketing', e)}
        ></cookie-preference-category>
      </div>
    `;
  }

  renderFooter() {
    return html`
      <footer class="cookie_preferences_footer">
        <div class="cookie_preferences_actions">
          <button
            type="button"
            class="cookie_preferences_btn cookie_preferences_btn_save"
            @click=${this.handleSavePreferences}
          >
            ${this.save_preferences_text}
          </button>
        </div>
      </footer>
    `;
  }
}

window.customElements.get('drawer-cookie-preferences') ||
  window.customElements.define(
    'drawer-cookie-preferences',
    DrawerCookiePreferences
  );
